import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Grid, Box, Typography } from '@mui/material';
import MpiTable from '../MpiTable';
import DynamicSimpleChecklist from './DynamicSimpleChecklist';
import _ from 'lodash';

const PDF_WIDTH_PX = 1450;

const SectionRenderer = ({
  sections,
  groupedItems,
  headers,
  sectionTotaling,
  active_vehicle,
  vehicle_id,
  default_labor_rate,
  onDelete,
  refreshVehicle,
  rowTotals,
  setRowTotals,
  tableRefs,
  form,
  addCustomRow,
  handleBulkActionConfirm,
  handleBulkActionButtonClick,
  work_item_presets,
  onDeleteAttachment,
  dispatch,
  current_user_profile_id,
  current_dealership,
  setError,
  confirmingTask,
  isDataReady,
  tab,
  sectionRefs,
  onApproveAllSection,
}) => {
  const sortedSections = useMemo(() => {
    return _.sortBy(sections || [], s => s.order ?? 999999);
  }, [sections]);

  const MAX_SECTIONS = 100;
  const paddedSections = useMemo(() => {
    const result = [...sortedSections];
    while (result.length < MAX_SECTIONS) {
      result.push({
        id: `empty-${result.length}`,
        label: '',
        layout: { mode: 'default', width: '100%', align: 'left' },
        order: result.length,
      });
    }
    return result.slice(0, MAX_SECTIONS);
  }, [sortedSections]);

  const groupedByAlign = useMemo(() => {
    const groups = { left: [], right: [] };
    paddedSections.forEach(section => {
      if (!section.id.startsWith('empty-')) {
        const align = section.layout?.align || 'left';
        groups[align === 'right' ? 'right' : 'left'].push(section);
      }
    });
    return groups;
  }, [paddedSections]);

  const isDisabled = useSelector(state => {
    const currentUser = state.current_user || {};
    const currentUserProfile = state.settings_users?.current_user_profile || {};
    const hasApprove = currentUser.approve_estimates || currentUserProfile.work_item_approve;
    const hasEdit = currentUserProfile.work_item_edit;
    const isAdmin = ['admin', 'super_admin'].includes(currentUser.position || '');
    return !(hasApprove || hasEdit || isAdmin);
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        width: '100%',
        maxWidth: `${PDF_WIDTH_PX}px`,
        mx: 'auto',
        px: { xs: 0, sm: 0 },
        py: 3,
        boxSizing: 'border-box',
      }}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          {groupedByAlign.left.map(section => (
            <SectionContent
              key={section.id}
              section={section}
              items={groupedItems[section.id] || []}
              headers={headers[section.id] || []}
              rule={sectionTotaling[section.id] || {}}
              active_vehicle={active_vehicle}
              vehicle_id={vehicle_id}
              default_labor_rate={default_labor_rate}
              onDelete={onDelete}
              refreshVehicle={refreshVehicle}
              rowTotals={rowTotals}
              setRowTotals={setRowTotals}
              tableRefs={tableRefs}
              form={form}
              addCustomRow={addCustomRow}
              handleBulkActionConfirm={handleBulkActionConfirm}
              handleBulkActionButtonClick={handleBulkActionButtonClick}
              work_item_presets={work_item_presets}
              onDeleteAttachment={onDeleteAttachment}
              dispatch={dispatch}
              current_user_profile_id={current_user_profile_id}
              current_dealership={current_dealership}
              setError={setError}
              confirmingTask={confirmingTask}
              isDataReady={isDataReady}
              tab={tab}
              isDisabled={isDisabled}
              sectionRefs={sectionRefs}
              onApproveAllSection={onApproveAllSection}
            />
          ))}
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          {groupedByAlign.right.map(section => (
            <SectionContent
              key={section.id}
              section={section}
              items={groupedItems[section.id] || []}
              headers={headers[section.id] || []}
              rule={sectionTotaling[section.id] || {}}
              active_vehicle={active_vehicle}
              vehicle_id={vehicle_id}
              default_labor_rate={default_labor_rate}
              onDelete={onDelete}
              refreshVehicle={refreshVehicle}
              rowTotals={rowTotals}
              setRowTotals={setRowTotals}
              tableRefs={tableRefs}
              form={form}
              addCustomRow={addCustomRow}
              handleBulkActionConfirm={handleBulkActionConfirm}
              handleBulkActionButtonClick={handleBulkActionButtonClick}
              work_item_presets={work_item_presets}
              onDeleteAttachment={onDeleteAttachment}
              dispatch={dispatch}
              current_user_profile_id={current_user_profile_id}
              current_dealership={current_dealership}
              setError={setError}
              confirmingTask={confirmingTask}
              isDataReady={isDataReady}
              tab={tab}
              isDisabled={isDisabled}
              sectionRefs={sectionRefs}
              onApproveAllSection={onApproveAllSection}
            />
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};

const SectionContent = ({
  section,
  items,
  headers,
  rule,
  active_vehicle,
  vehicle_id,
  default_labor_rate,
  onDelete,
  refreshVehicle,
  rowTotals,
  setRowTotals,
  tableRefs,
  form,
  addCustomRow,
  handleBulkActionConfirm,
  handleBulkActionButtonClick,
  work_item_presets,
  onDeleteAttachment,
  dispatch,
  current_user_profile_id,
  current_dealership,
  setError,
  confirmingTask,
  isDataReady,
  tab,
  isDisabled,
  sectionRefs,
  onApproveAllSection,
}) => {
  const layout = section.layout || { mode: 'default', width: '100%', align: 'left' };
  const mode = layout.mode || 'default';
  const sectionBg = section.background || { color: '', image: '' };
  const headerColor = section.headerColor || '#f5f5f5';
  const fontColor = section.fontColor || '#000000';
  const headerFontColor = section.headerFontColor || '#000000';

  const containerSx = {
    width: '100%',
    mb: 4,
    borderRadius: 2,
    bgcolor: sectionBg.color || 'transparent',
    backgroundImage: sectionBg.image ? `url(${sectionBg.image})` : 'none',
    fontColor: headerFontColor,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'relative',
    '&::before': sectionBg.image ? {
      content: '""',
      position: 'absolute',
      inset: 0,
      bgcolor: 'rgba(255,255,255,0.85)',
      borderRadius: 2,
      zIndex: 0,
    } : {},
  };

  const innerSx = { position: 'relative', zIndex: 1, color: fontColor };

  // Attach ref to the outer Box
  const refCallback = el => {
    if (sectionRefs && section?.id) {
      sectionRefs.current[section.id] = el;
    }
  };

  if (mode === 'simple') {
    console.log('Approve All button - onApproveAllSection prop:', typeof onApproveAllSection, onApproveAllSection);
    return (
      <Box ref={refCallback} data-section-id={section.id} sx={containerSx}>
        <Box sx={innerSx}>
          <DynamicSimpleChecklist
            items={items}
            headers={headers}
            sectionId={section.id}
            sectionLabel={section.label}
            active_vehicle={active_vehicle}
            vehicle_id={vehicle_id}
            dispatch={dispatch}
            refreshVehicle={refreshVehicle}
            setError={setError}
            current_user_profile_id={current_user_profile_id}
            addCustomRow={addCustomRow}
            headerColor={headerColor}
            fontColor={fontColor}
            headerFontColor={headerFontColor}
            onApproveAllSection={onApproveAllSection}
            work_item_presets={work_item_presets}
          />
        </Box>
      </Box>
    );
  }

  // Non-simple mode (pricing/table)
  return (
    <Box ref={refCallback} data-section-id={section.id} sx={containerSx}>
      <Box sx={innerSx}>
        <Box
          sx={{
            bgcolor: headerColor,
            px: 2,
            py: 1.2,
            borderRadius: '8px 8px 0 0',
            borderLeft: '5px solid #1976d2',
            display: 'flex',
            alignItems: 'center',
            minHeight: 52,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              textTransform: 'capitalize',
              fontSize: '1.1rem',
              color: headerFontColor,
              flex: 1,
              pr: 2,
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {section.label}
          </Typography>
        </Box>

        <Box sx={{ p: 2, pt: 1 }}>
          <MpiTable
            items={items}
            dynamicHeaders={headers}
            sectionRule={rule}
            vehicleId={active_vehicle.id || vehicle_id}
            defaultLaborRate={default_labor_rate}
            onDelete={onDelete}
            onRefresh={refreshVehicle}
            rowTotals={rowTotals}
            setRowTotals={setRowTotals}
            tableRef={tableRefs.current?.[section.id]}
            enableRowSelection
            form={form}
            section={section.id}
            work_item_presets={work_item_presets}
            current_user_profile_id={current_user_profile_id}
            current_dealership={current_dealership}
            isDisabled={isDisabled}
            onDeleteAttachment={onDeleteAttachment}
            confirmingTask={confirmingTask}
            handleBulkActionConfirm={handleBulkActionConfirm}
            handleBulkActionButtonClick={handleBulkActionButtonClick}
            dispatch={dispatch}
            setError={setError}
            isDataReady={isDataReady}
            tab={tab}
            onApproveAllSection={onApproveAllSection}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SectionRenderer;